import { useState, useRef, useEffect } from "preact/hooks";
import {
  MessageSquare,
  Send,
  Menu,
  Plus,
  Phone,
  Shield,
  X,
  Sparkles,
  User,
  Bot,
} from "lucide-preact";
import { WidgetConfig } from "@/worker/schemas";
import { formatPhoneToE164 } from "./utils/phone";
import {
  getStoredThreadId,
  storeThreadId,
  clearStoredThreadId,
} from "./utils/thread";
import { marked } from "marked";
import { generateAnonymousId } from "@/shared/anonymous-session";
import { type MessageMedia as MediaItem } from "@/worker/schemas";
import { MessageMedia } from "./MessageMedia";
import { HandoffSendForm } from "./HandoffSendForm";
import { type HandoffSendState } from "@/shared/handoff-send";
import { plainWhatsAppLink } from "@/widget/utils/whatsapp-link";
import {
  handoffAfterMint,
  handoffAfterReply,
  handoffAfterRestore,
  handoffLanguage,
  handoffLink,
  requestHandoffMint,
  type Handoff,
  type HandoffLinkData,
} from "@/shared/whatsapp-handoff";

// Mock types
interface Message {
  id: string;
  content: string;
  role: "user" | "assistant";
  createdAt: number;
  // Agent-attached photos (PLT-1783); absent on a text-only reply.
  media?: MediaItem[];
  // The coded WhatsApp link the agent offered on this reply (PLT-2164).
  handoff?: HandoffLinkData;
}

interface AppProps {
  config: WidgetConfig;
}

export function App() {
  // State
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  // Single-flight for lazy session creation: concurrent first sends must
  // share ONE createSession call (see handleSendMessage).
  const sessionPromiseRef = useRef<Promise<string | null> | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [phoneInput, setPhoneInput] = useState("");
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [logoError, setLogoError] = useState(false);
  // The coded WhatsApp link and the thread it was minted for (PLT-2164).
  const [handoff, setHandoff] = useState<Handoff | null>(null);
  // True after the visitor tapped a WhatsApp entry on this page.
  const [handedOff, setHandedOff] = useState(false);
  // Where the "or get a message" form stands for the active thread (PLT-2165).
  const [handoffSend, setHandoffSend] = useState<HandoffSendState | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState<string>(() => {
    // Initialize from document lang or default to 'ar'
    const htmlLang = document.documentElement.lang;
    if (htmlLang) {
      const langCode = htmlLang.split("-")[0];
      return langCode === "ar" ? "ar" : "en";
    }
    return "ar";
  });

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const config = (window as any).tkanaChatConfig || {};
  const primaryColor = config.branding?.primaryColor || "#3b82f6"; // Default blue
  const logoUrl = config.logoUrl;
  const isArabic = currentLanguage === "ar";
  // Same pick as the widget header: the name in the page language.
  const companyName: string =
    (typeof config.companyName === "string"
      ? config.companyName
      : isArabic
        ? config.companyName?.ar || config.companyName?.en
        : config.companyName?.en || config.companyName?.ar) ||
    config.organizationName ||
    config.widgetTitle ||
    "";

  // Update document language when language changes (but not direction)
  useEffect(() => {
    document.documentElement.lang = currentLanguage;
  }, [currentLanguage]);

  // Load threadId and phone from storage on mount, or auto-create session if hidePhoneNumber is true
  useEffect(() => {
    if (!config.id) return;

    const storedThreadId = getStoredThreadId(config.id);
    const storedPhone = localStorage.getItem(`phone-${config.id}`);

    if (storedThreadId) {
      config.threadId = storedThreadId;
    }

    if (storedPhone) {
      setPhoneNumber(storedPhone);
    }

    // Load messages if threadId exists. When hidePhoneNumber is set, the
    // anonymous session is created lazily on the first send (see
    // handleSendMessage) — merely opening the chat page must not create a
    // customer/conversation server-side.
    if (storedThreadId) {
      loadMessages(storedThreadId);
    }
  }, []);

  // Helper to darken color for hover states (simplified)
  // In a real app we might use 'color' package, here we just use the same color or rely on opacity

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  // Handle sending a message
  const handleSendMessage = async (
    content: string,
    skipPhoneCheck: boolean = false
  ) => {
    if (!content.trim()) return;

    // Skip phone check if hidePhoneNumber is enabled
    if (!phoneNumber && !skipPhoneCheck && !config.hidePhoneNumber) {
      setPendingMessage(content);
      setPhoneError(null);
      setShowPhoneModal(true);
      return;
    }

    // Add user message immediately
    const userMsg: Message = {
      id: Date.now().toString(),
      content: content,
      role: "user",
      createdAt: Date.now(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInputValue("");
    setIsTyping(true);

    try {
      if (!config.url) {
        throw new Error("Missing configuration");
      }

      // Lazily create the session on the first send: with hidePhoneNumber the
      // visitor is anonymous and no session exists until they actually write.
      // Creation is single-flighted through sessionPromiseRef — nothing stops
      // a second send while the first is still awaiting createSession, and two
      // unguarded creates could mint two conversations with the client
      // last-writer-wins on which threadId survives.
      if (!config.threadId) {
        if (!sessionPromiseRef.current) {
          sessionPromiseRef.current = (async () => {
            // Resolve the identity BEFORE calling createSession and persist it
            // immediately: if the server creates the session but the response
            // is lost, the retry must reuse the same identity — a freshly
            // minted id per attempt would create a second customer + orphaned
            // conversation.
            let sessionPhone = phoneNumber;
            if (!sessionPhone && config.id) {
              sessionPhone = localStorage.getItem(`phone-${config.id}`) || "";
            }
            if (!sessionPhone) {
              sessionPhone = generateAnonymousId();
              if (config.id) {
                localStorage.setItem(`phone-${config.id}`, sessionPhone);
              }
            }
            if (!phoneNumber) {
              setPhoneNumber(sessionPhone);
            }
            return createSession(sessionPhone);
          })();
        }

        let threadId: string | null = null;
        try {
          threadId = await sessionPromiseRef.current;
        } finally {
          // Success: config.threadId below stops future entries into this
          // block. Failure: clearing lets the next send try again.
          sessionPromiseRef.current = null;
        }
        if (!threadId) {
          throw new Error("Failed to create session");
        }
        config.threadId = threadId;
        if (config.id) {
          storeThreadId(config.id, threadId);
        }
      }

      const requestData = {
        message: content,
        threadId: config.threadId,
        timestamp: Date.now(),
        pageUrl: window.location.href,
        language: handoffLanguage(currentLanguage),
      };

      const response = await fetch(config.url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-widget-id": config.id,
        },
        body: JSON.stringify(requestData),
      });

      if (response.ok) {
        const data = (await response.json()) as any;
        const content = data.text || data.message || data.content;

        // A media-only turn is NOT empty: the agent can attach a photo with no
        // text, so render on either signal (PLT-1783). Array.isArray rather
        // than truthiness, so a malformed payload degrades to a text reply
        // instead of throwing inside the renderer.
        const media: MediaItem[] | undefined =
          Array.isArray(data.media) && data.media.length > 0
            ? data.media
            : undefined;

        const handoffReplyUrl: string | null =
          typeof data.handoff?.url === "string" ? data.handoff.url : null;
        const handoffReplyExpiresAt: string | null =
          typeof data.handoff?.expiresAt === "string"
            ? data.handoff.expiresAt
            : null;

        if (content || media) {
          const aiMsg: Message = {
            id: data.id || Date.now().toString(),
            content: content || "",
            role: "assistant",
            createdAt: data.timestamp || Date.now(),
            ...(media ? { media } : {}),
            ...(handoffReplyUrl
              ? {
                  handoff: {
                    url: handoffReplyUrl,
                    expiresAt: handoffReplyExpiresAt,
                  },
                }
              : {}),
          };
          setMessages((prev) => [...prev, aiMsg]);
        }

        // A code belongs to one thread: drop a link minted for an earlier
        // thread and mint again (PLT-2164).
        const replyThreadId: string | null =
          typeof data.threadId === "string" ? data.threadId : config.threadId;
        // Adopt the thread the server answered on, as the floating widget does.
        // A reply past the reopen window comes back on a new thread, and the
        // header icon and the agent's card read their code for config.threadId.
        if (replyThreadId && replyThreadId !== config.threadId) {
          config.threadId = replyThreadId;
          if (config.id) {
            storeThreadId(config.id, replyThreadId);
          }
        }
        if (replyThreadId) {
          const next = handoffAfterReply(handoff, {
            threadId: replyThreadId,
            responseUrl: handoffReplyUrl,
            responseExpiresAt: handoffReplyExpiresAt,
            hasNumber: Boolean(config.whatsappNumber),
          });
          setHandoff(next.handoff);
          // Background mint so the icon already carries the code when
          // tapped. A failure leaves the plain link in place.
          if (next.mint) mintInBackground(replyThreadId);
        }
      }
    } catch (error) {
      console.error("Failed to send message", error);
      const errorMsg: Message = {
        id: Date.now().toString(),
        content: "Sorry, I couldn't connect to the server.",
        role: "assistant",
        createdAt: Date.now(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setIsTyping(false);
    }
  };

  const createSession = async (phone: string) => {
    try {
      if (!config.url) {
        console.error("No URL provided for chat widget");
        throw new Error("No URL provided for chat widget");
      }

      // Match widget implementation: replace /chat with /chat/session
      const sessionUrl = config.url.replace(/\/chat\/?$/, "/chat/session");
      const formattedPhone = formatPhoneToE164(phone);

      console.log("Creating session:", {
        sessionUrl,
        formattedPhone,
        widgetId: config.id,
      });

      const response = await fetch(sessionUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-widget-id": config.id,
        },
        body: JSON.stringify({
          user: {
            phone: formattedPhone,
            ...(config.user || {}),
          },
          metadata: {
            pageUrl: window.location.href,
            userAgent: navigator.userAgent,
          },
        }),
      });

      console.log("Session response status:", response.status);

      if (!response.ok) {
        const error = await response.text();
        console.error("Session creation failed with error:", error);
        throw new Error(`Failed to create session: ${error}`);
      }

      const data = (await response.json()) as any;
      console.log("Session created successfully:", data);
      return data.threadId;
    } catch (e) {
      console.error("Session creation failed with exception:", e);
      return null;
    }
  };

  // The link a WhatsApp entry opens (PLT-2164): the live code for the
  // active thread, else the plain greeting. A card never uses its own link,
  // which belongs to the thread it arrived in. A card hides when the result
  // is not coded, since only a code carries the chat.
  const whatsAppLink = () =>
    handoffLink({
      handoff,
      threadId: config.threadId,
      plainUrl: plainWhatsAppLink(config.whatsappNumber ?? "", currentLanguage),
    });

  // Mint the code for `threadId`. A failed mint, or one that comes back after
  // the thread changed, leaves the plain link in place.
  const mintInBackground = (threadId: string) => {
    void requestHandoffMint({
      chatUrl: config.url,
      widgetId: config.id,
      threadId,
      language: handoffLanguage(currentLanguage),
    }).then((minted) =>
      setHandoff((current) =>
        handoffAfterMint(current, {
          url: minted?.url,
          expiresAt: minted?.expiresAt,
          mintedFor: threadId,
          activeThreadId: config.threadId ?? null,
        }),
      ),
    );
  };

  // Load messages from API
  const loadMessages = async (threadId: string) => {
    try {
      if (!config.url) {
        throw new Error("Missing configuration");
      }

      // API expects GET /api/chat?threadId=xxx
      const messagesUrl = `${config.url}?threadId=${threadId}`;
      const response = await fetch(messagesUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-widget-id": config.id,
        },
      });

      if (response.ok) {
        const data = (await response.json()) as any;
        if (data && Array.isArray(data)) {
          // API returns MessageData[] with {id, text, timestamp, from}
          const loadedMessages: Message[] = data.map((msg: any) => ({
            id: msg.id || Date.now().toString(),
            content: msg.text || msg.content || msg.message || "",
            role: msg.from === "system" ? "assistant" : "user",
            createdAt: msg.timestamp || Date.now(),
            // The worker forwards the persisted attachments, so a reloaded
            // thread still shows the photos the agent sent (PLT-1783).
            ...(Array.isArray(msg.media) && msg.media.length > 0
              ? { media: msg.media }
              : {}),
          }));
          setMessages(loadedMessages);
          // A restored thread mints as a reply does, so the icon carries the
          // code after a page load (PLT-2164).
          const next = handoffAfterRestore(handoff, {
            threadId,
            senders: loadedMessages.map((message) => message.role),
            hasNumber: Boolean(config.whatsappNumber),
          });
          setHandoff(next.handoff);
          if (next.mint) mintInBackground(threadId);
        }
      }
    } catch (error) {
      console.error("Failed to load messages", error);
    }
  };

  const handlePhoneSubmit = async (e: Event) => {
    e.preventDefault();
    if (phoneInput.length < 8 || isVerifying) return;

    setIsVerifying(true);
    setPhoneError(null);

    try {
      console.log("handlePhoneSubmit: Starting phone verification");
      // Create session first
      const threadId = await createSession(phoneInput);
      console.log("handlePhoneSubmit: Received threadId:", threadId);

      if (threadId) {
        console.log(
          "handlePhoneSubmit: ThreadId is valid, saving and closing modal"
        );
        config.threadId = threadId; // Update global config

        // Save to storage with utility functions
        if (config.id) {
          storeThreadId(config.id, threadId);
          localStorage.setItem(`phone-${config.id}`, phoneInput);
          console.log("handlePhoneSubmit: Saved to localStorage");
        }

        // Store pending message before clearing it
        const messageToBeSent = pendingMessage;

        // Update states in correct order
        setPhoneNumber(phoneInput);
        setPendingMessage(null);
        setShowPhoneModal(false);
        console.log("handlePhoneSubmit: Modal closed, phone number set");

        // Send pending message if it exists, using the stored value
        if (messageToBeSent) {
          console.log(
            "handlePhoneSubmit: Sending pending message:",
            messageToBeSent
          );
          // Skip phone check since we just verified it
          await handleSendMessage(messageToBeSent, true);
        }
        console.log("handlePhoneSubmit: All done!");
      } else {
        console.error("handlePhoneSubmit: No threadId returned, showing error");
        setPhoneError(
          isArabic
            ? "فشل التحقق من رقم الهاتف. يرجى التحقق من الرقم والمحاولة مرة أخرى."
            : "Failed to verify phone number. Please check the number and try again."
        );
      }
    } catch (error) {
      console.error("handlePhoneSubmit: Exception caught:", error);
      setPhoneError(
        isArabic
          ? "حدث خطأ. يرجى المحاولة مرة أخرى."
          : "An error occurred. Please try again."
      );
    } finally {
      setIsVerifying(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(inputValue);
    }
  };

  return (
    <div
      className="flex bg-white font-sans text-gray-900 overflow-hidden flex-col"
      style={{
        height: "100%",
        boxSizing: "border-box",
        paddingTop: "env(safe-area-inset-top)",
        paddingLeft: "env(safe-area-inset-left)",
        paddingRight: "env(safe-area-inset-right)",
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-white z-10 shadow-sm">
        <div className="flex items-center gap-3">
          {logoUrl && !logoError ? (
            <img
              src={logoUrl}
              alt={config.organizationName || "Organization"}
              className="max-w-24 max-h-16 object-contain "
              onError={() => setLogoError(true)}
            />
          ) : (
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold shadow-sm"
              style={{ backgroundColor: primaryColor }}
            >
              <Sparkles size={20} />
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
        {config.whatsappNumber && (
          <a
            href={whatsAppLink().href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => {
              // Read again on tap: the page may have stayed open past the
              // code's expiry. Only a coded link moves the chat, so only it
              // shows the notice.
              const link = whatsAppLink();
              event.currentTarget.href = link.href;
              if (link.coded) setHandedOff(true);
            }}
            aria-label={isArabic ? "أكمل على واتساب" : "Continue on WhatsApp"}
            title={isArabic ? "أكمل على واتساب" : "Continue on WhatsApp"}
            className="flex items-center justify-center rounded-full p-2 text-[#25d366] hover:bg-[#25d366]/10"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
              <path
                fill="currentColor"
                d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"
              />
            </svg>
          </a>
        )}

        {/* Language Switcher */}
        <div
          className="flex gap-0.5 bg-white rounded-lg p-0.5 border-2 shadow-sm"
          style={{ borderColor: `${primaryColor}20` }}
        >
          <button
            onClick={() => setCurrentLanguage("en")}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all min-w-[32px] ${
              currentLanguage === "en"
                ? "text-white shadow-sm"
                : "text-gray-600 hover:bg-gray-50"
            }`}
            style={
              currentLanguage === "en" ? { backgroundColor: primaryColor } : {}
            }
          >
            EN
          </button>
          <button
            onClick={() => setCurrentLanguage("ar")}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all min-w-[32px] ${
              currentLanguage === "ar"
                ? "text-white shadow-sm"
                : "text-gray-600 hover:bg-gray-50"
            }`}
            style={
              currentLanguage === "ar" ? { backgroundColor: primaryColor } : {}
            }
          >
            AR
          </button>
        </div>
        </div>
      </div>

      {/* Chat Area */}
      <div
        className={
          messages.length === 0
            ? "flex-1 flex items-center justify-center p-4 md:p-8 overflow-hidden"
            : "flex-1 p-4 md:p-8 space-y-6 overflow-y-auto"
        }
        style={{ overscrollBehavior: "contain" }}
      >
        {messages.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center w-full text-center max-w-lg mx-auto px-4 opacity-0 animate-in fade-in zoom-in duration-500 fill-mode-forwards"
            style={{ animationDelay: "100ms", opacity: 1 }}
          >
            <div
              className="w-20 h-20 rounded-2xl shadow-lg flex items-center justify-center mb-6 text-white transform transition-transform hover:scale-105"
              style={{ backgroundColor: primaryColor }}
            >
              <Sparkles size={40} />
            </div>
            <h2 className="text-2xl font-bold mb-3 text-gray-900">
              {typeof config.greetingMessage === "object"
                ? config.greetingMessage[isArabic ? "ar" : "en"] ||
                  config.greetingMessage.en ||
                  config.greetingMessage.ar
                : config.greetingMessage ||
                  (isArabic
                    ? "كيف يمكنني مساعدتك اليوم؟"
                    : "How can I help you today?")}
            </h2>
            <p className="text-gray-500">
              {isArabic
                ? "تحقق من رقمك للبدء في الدردشة معنا."
                : "Verify your number to start chatting with us."}
            </p>
          </div>
        ) : (
          <div className="max-w-3xl mx-auto space-y-6 pb-24">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex gap-4 ${
                  msg.role === "user" ? "justify-end" : "justify-start"
                }`}
              >
                {msg.role === "assistant" && (
                  <div className="w-8 h-8 rounded-full border border-gray-100 flex items-center justify-center flex-shrink-0 mt-1 bg-white shadow-sm">
                    <Bot size={16} style={{ color: primaryColor }} />
                  </div>
                )}

                <div
                  className={`relative px-5 py-3.5 rounded-2xl max-w-[85%] md:max-w-[75%] leading-relaxed shadow-sm ${
                    msg.role === "user"
                      ? "text-white rounded-tr-sm whitespace-pre-wrap"
                      : "bg-white border border-gray-200 text-gray-800 rounded-tl-sm"
                  }`}
                  style={
                    msg.role === "user" ? { backgroundColor: primaryColor } : {}
                  }
                >
                  {/* Photos above the text, matching the order WhatsApp
                      delivers them in (image payloads first, then the reply). */}
                  {msg.media && msg.media.length > 0 && (
                    <MessageMedia media={msg.media} isArabic={isArabic} />
                  )}
                  {msg.role === "assistant" ? (
                    msg.content && (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: marked.parse(msg.content) as string,
                        }}
                      />
                    )
                  ) : (
                    msg.content
                  )}
                  {msg.handoff?.url && whatsAppLink().coded && (
                    <div className="mt-3 flex flex-col gap-2 rounded-lg border border-gray-200 bg-white p-3">
                      <a
                        href={whatsAppLink().href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(event) => {
                          const link = whatsAppLink();
                          event.currentTarget.href = link.href;
                          if (link.coded) setHandedOff(true);
                        }}
                        className="inline-flex items-center justify-center rounded-lg bg-[#25d366] px-3 py-2 text-sm font-medium !text-white no-underline hover:bg-[#128c7e]"
                      >
                        {isArabic ? "أكمل على واتساب" : "Continue on WhatsApp"}
                      </a>
                      <small className="text-xs leading-snug text-gray-500">
                        {isArabic
                          ? `يفتح واتساب برسالة جاهزة للإرسال إلى ${companyName}. المحادثة تنتقل معك.`
                          : `Opens WhatsApp with a message ready to send to ${companyName}. This chat comes with you.`}
                      </small>
                      {config.whatsappTemplate && (
                        <HandoffSendForm
                          chatUrl={config.url}
                          widgetId={config.id}
                          threadId={config.threadId ?? null}
                          callingCode={config.whatsappTemplate.callingCode ?? null}
                          language={currentLanguage}
                          companyName={companyName}
                          primaryColor={primaryColor}
                          state={handoffSend}
                          onState={setHandoffSend}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Typing Indicator */}
            {isTyping && (
              <div className="flex gap-4 justify-start">
                <div className="w-8 h-8 rounded-full border border-gray-100 flex items-center justify-center flex-shrink-0 mt-1 bg-white shadow-sm">
                  <Bot size={16} style={{ color: primaryColor }} />
                </div>
                <div className="relative px-5 py-3.5 rounded-2xl bg-white border border-gray-200 shadow-sm">
                  <div className="flex gap-1.5">
                    <div
                      className="w-2 h-2 rounded-full animate-bounce"
                      style={{
                        backgroundColor: primaryColor,
                        animationDelay: "0ms",
                        animationDuration: "1000ms",
                      }}
                    />
                    <div
                      className="w-2 h-2 rounded-full animate-bounce"
                      style={{
                        backgroundColor: primaryColor,
                        animationDelay: "150ms",
                        animationDuration: "1000ms",
                      }}
                    />
                    <div
                      className="w-2 h-2 rounded-full animate-bounce"
                      style={{
                        backgroundColor: primaryColor,
                        animationDelay: "300ms",
                        animationDuration: "1000ms",
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {handedOff && (
              <div className="mx-auto w-fit rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-500">
                {isArabic
                  ? "محادثتك مستمرة على واتساب"
                  : "Your chat continues on WhatsApp"}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input Area */}
      <div
        className="sticky bottom-0 bg-white border-t border-gray-100 p-4 pt-4"
        style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
      >
        <div className="max-w-3xl mx-auto relative">
          <div
            className="relative flex items-end gap-2 bg-gray-50 border border-gray-200 rounded-2xl p-2 shadow-sm focus-within:ring-2 focus-within:ring-opacity-50 transition-all focus-within:border-transparent"
            style={{ "--tw-ring-color": primaryColor } as any}
          >
            <textarea
              ref={inputRef}
              value={inputValue}
              onInput={(e) =>
                setInputValue((e.target as HTMLInputElement).value)
              }
              onKeyDown={handleKeyDown}
              placeholder={isArabic ? "اكتب رسالتك..." : "Message..."}
              className="w-full bg-transparent border-none focus:ring-0 resize-none max-h-32 min-h-[44px] py-3 px-2 text-base outline-none ring-0 placeholder:text-gray-400"
              style={{ height: "auto" }}
              rows={1}
            />
            <button
              onClick={() => handleSendMessage(inputValue)}
              disabled={!inputValue.trim()}
              className={`p-2.5 rounded-xl mb-1 flex-shrink-0 transition-all ${
                inputValue.trim()
                  ? "text-white shadow-md hover:opactiy-90"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed"
              }`}
              style={inputValue.trim() ? { backgroundColor: primaryColor } : {}}
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Phone Number Modal */}
      {showPhoneModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-gray-50">
                  <Shield size={24} style={{ color: primaryColor }} />
                </div>
                <h3 className="text-lg font-bold text-gray-900">
                  {isArabic ? "التحقق مطلوب" : "Verification Required"}
                </h3>
              </div>

              <p className="text-gray-600 mb-6 leading-relaxed">
                {isArabic
                  ? "يرجى التحقق من رقم هاتفك لبدء هذه المحادثة. يساعدنا هذا في الحفاظ على أمان المنصة ويسمح لنا بالتواصل معك إذا انقطع الاتصال."
                  : "Please verify your phone number to start this conversation. This helps us keep the platform safe and allows us to reach you if you get disconnected."}
              </p>

              <form onSubmit={handlePhoneSubmit}>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isArabic ? "رقم الهاتف" : "Phone Number"}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Phone size={18} className="text-gray-400" />
                    </div>
                    <input
                      type="tel"
                      value={phoneInput}
                      onInput={(e) => {
                        setPhoneInput((e.target as HTMLInputElement).value);
                        setPhoneError(null);
                      }}
                      className={`block w-full pl-10 pr-3 py-3 border rounded-xl focus:ring-2 focus:ring-opacity-50 focus:border-transparent transition-all outline-none ${
                        phoneError ? "border-red-400" : "border-gray-300"
                      }`}
                      style={{ "--tw-ring-color": primaryColor } as any}
                      placeholder="+1 (555) 000-0000"
                      required
                      autoFocus
                    />
                  </div>
                  {phoneError && (
                    <p className="mt-2 text-sm text-red-600">{phoneError}</p>
                  )}
                </div>

                <div className="flex gap-3 justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPhoneModal(false);
                      setPhoneError(null);
                    }}
                    className="px-5 py-2.5 rounded-xl text-gray-600 hover:bg-gray-100 font-medium transition-colors"
                  >
                    {isArabic ? "إلغاء" : "Cancel"}
                  </button>
                  <button
                    type="submit"
                    disabled={phoneInput.length < 3 || isVerifying}
                    className="px-5 py-2.5 rounded-xl text-white font-medium shadow-md transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {isVerifying
                      ? isArabic
                        ? "جاري التحقق..."
                        : "Verifying..."
                      : isArabic
                      ? "التحقق والمتابعة"
                      : "Verify & Continue"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
